import type { AuthResponse, PublicUser } from '@foosball/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { api, setAccessToken, setRefreshHandler } from '../api/client';

interface AuthContextValue {
  user: PublicUser | null;
  /** `loading` until the initial session restore settles — routes must wait. */
  status: 'loading' | 'ready';
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    displayName: string;
    password: string;
    inviteCode?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const queryClient = useQueryClient();

  // Collapses concurrent refreshes into one request. Without this, several
  // queries failing with 401 at once would each rotate the token, and all but
  // the first would look like a replay — which now revokes the whole family.
  const inFlight = useRef<Promise<boolean> | null>(null);

  const applySession = useCallback((data: AuthResponse) => {
    setAccessToken(data.accessToken);
    setUser(data.user);
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  const doRefresh = useCallback((): Promise<boolean> => {
    // Raw fetch, not the api wrapper: the wrapper retries on 401 by calling
    // this function, which would recurse.
    inFlight.current ??= (async () => {
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
        });

        if (!response.ok) {
          clearSession();
          return false;
        }

        applySession((await response.json()) as AuthResponse);
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        inFlight.current = null;
      }
    })();

    return inFlight.current;
  }, [applySession, clearSession]);

  useEffect(() => {
    setRefreshHandler(doRefresh);

    // The access token lives in memory only, so a page reload always starts
    // signed out. The httpOnly refresh cookie is what restores the session.
    void doRefresh().finally(() => setStatus('ready'));

    return () => setRefreshHandler(null);
  }, [doRefresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      applySession(await api.post<AuthResponse>('/api/auth/login', { email, password }));
    },
    [applySession],
  );

  const register = useCallback(
    async (input: { email: string; displayName: string; password: string; inviteCode?: string }) => {
      applySession(await api.post<AuthResponse>('/api/auth/register', input));
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      clearSession();
      // Drop every cached query so the next user cannot see the last one's data.
      queryClient.clear();
    }
  }, [clearSession, queryClient]);

  const value = useMemo(
    () => ({ user, status, login, register, logout }),
    [user, status, login, register, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
